/**
 * SubscribeMenuItem — subscribe/unsubscribe toggle shown inside a space action menu.
 * Self-contained: handles its own subscription state check on open.
 */
import React, { useState, useEffect, useRef } from "react";
import { Menu, Spin, message } from "antd";
import { BellOutlined, BellFilled } from "@ant-design/icons";
import { useMsal } from "@azure/msal-react";
import { subscribeToSpace, unsubscribeFromSpace, checkSubscription } from "./gridApi";
import { getLoggedInUser } from "utils/utils";
import { getAgCreds, IGetAgCredsResponse } from "components/ObjectList/objectListApi";
import { IGetAgCreds } from "components/ObjectList";
import type { SpaceActionRecord } from "./SpaceActionMenu";

interface SubscribeMenuItemProps {
  record: SpaceActionRecord;
  domainName: string;
  /** Whether the parent dropdown is currently open — triggers the subscription check. */
  isOpen: boolean;
  onClose: () => void;
}

const SubscribeMenuItem: React.FC<SubscribeMenuItemProps> = ({
  record,
  domainName,
  isOpen,
  onClose,
}) => {
  const msalInstance = useMsal();
  const [isChecking, setIsChecking] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const hasChecked = useRef(false);

  useEffect(() => {
    if (!isOpen || hasChecked.current) return;
    hasChecked.current = true;
    const loggedInUser = getLoggedInUser();
    setIsChecking(true);
    checkSubscription(msalInstance, record.spaceName, loggedInUser)
      .then((response) => { setIsSubscribed(response.isSubscribed); })
      .catch((err) => { console.error("Error checking subscription:", err); })
      .finally(() => { setIsChecking(false); });
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = async () => {
    const loggedInUser = getLoggedInUser();
    setIsToggling(true);
    try {
      const payload: IGetAgCreds = {
        bucket_name: record.bucketName,
        region: record.regionName,
        data_account_id: record.data_account_id,
        space_name: record.spaceName,
        access_level: "READWRITE",
      };
      const credsResponse = await getAgCreds(msalInstance, payload);
      if (credsResponse === false || !credsResponse) {
        message.error("You do not have access to this project. Please contact the project owner for access.");
        return;
      }
      const creds = credsResponse as IGetAgCredsResponse;
      const userRole = creds.user_role?.toLowerCase();
      const accessLevel = creds.access_level?.toUpperCase();
      const hasAccess =
        userRole === "ro" || userRole === "rw" || userRole === "owner" || userRole === "read" ||
        accessLevel === "READ" || accessLevel === "READWRITE";
      if (!hasAccess) {
        message.error("You do not have access to this project. Please contact the project owner for access.");
        return;
      }
      const subscriptionPayload = {
        user_email: loggedInUser,
        space_name: record.spaceName,
        domain_name: domainName,
      };
      if (isSubscribed) {
        const response = await unsubscribeFromSpace(msalInstance, subscriptionPayload);
        if (response.success) {
          setIsSubscribed(false);
          hasChecked.current = false;
          message.success(`Successfully unsubscribed from ${record.spaceNameMasked || record.spaceName}`);
        } else {
          message.error(response.error || "Failed to unsubscribe. Please try again.");
        }
      } else {
        const response = await subscribeToSpace(msalInstance, subscriptionPayload);
        if (response.success) {
          setIsSubscribed(true);
          hasChecked.current = false;
          message.success(`Successfully subscribed to ${record.spaceNameMasked || record.spaceName}`);
        } else {
          message.error(response.error || "Failed to subscribe. Please try again.");
        }
      }
    } catch {
      message.error("You do not have access to this project. Please contact the project owner for access.");
    } finally {
      setIsToggling(false);
      onClose();
    }
  };

  return (
    <Menu.Item
      key="subscribeSpace"
      disabled={isChecking || isToggling}
      style={{ color: "#1677ff", fontWeight: 500, fontSize: 15, paddingLeft: 16 }}
      onClick={(e) => {
        e.domEvent.stopPropagation();
        handleToggle();
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {(isChecking || isToggling) ? (
          <Spin size="small" />
        ) : isSubscribed ? (
          <BellFilled style={{ fontSize: 16, color: "#52c41a" }} />
        ) : (
          <BellOutlined style={{ fontSize: 16 }} />
        )}
        {isChecking ? "Checking..." : isToggling ? "Updating..." : isSubscribed ? "Unsubscribe" : "Subscribe space"}
      </span>
    </Menu.Item>
  );
};

export default SubscribeMenuItem;
